import { jsx, jsxs } from 'react/jsx-runtime';
import { CheckmarkIcon } from '@flex-design-system/legacy-flex-icons';
import { styled } from '@flex-design-system/stitches-react';
import { composeEventHandlers } from '@radix-ui/primitive';
import { useControllableState } from '@radix-ui/react-use-controllable-state';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { forwardRef, useMemo, isValidElement, useState } from 'react';
import { Legacy_Dropdown } from '../../legacy-dropdown/Dropdown.mjs';
import BubbleSelect from '../BubbleSelect.mjs';
import { AffixRoot } from './components/AffixRoot.mjs';
import { SelectCapturedActiveValue } from './components/CapturedActiveValue.mjs';
import { SelectContent } from './components/Content.mjs';
import { SelectGroup } from './components/Group.mjs';
import { Option } from './components/Option.mjs';
import { OptionDescription } from './components/OptionDescription.mjs';
import { SelectOptionIndicator } from './components/OptionIndicator.mjs';
import { OptionTitle } from './components/OptionTitle.mjs';
import { OptionTitle_legacy } from './components/OptionTitle_legacy.mjs';
import { SelectSearchEmpty } from './components/SearchEmpty.mjs';
import SearchInput from './components/SearchInput.mjs';
import { SelectSeparator } from './components/Separator.mjs';
import StaticInput from './components/StaticInput.mjs';
import { SelectSubContent } from './components/SubContent.mjs';
import { SelectSubMenuTrigger } from './components/SubMenuTrigger.mjs';
import { Trigger } from './components/Trigger.mjs';
import { SelectValue } from './components/Value.mjs';
import { SelectProvider, useSelectContext, useSelectSearchContext } from './context.mjs';
import { SelectSearchRoot } from './contexts/search-root.mjs';
import { optionItemContentStyle, optionLegacyItemContentStyle } from './select.vanilla.mjs';

const InternalSelect = (props) => {
  const {
    open: openProp,
    defaultOpen,
    value: valueProp,
    defaultValue,
    onValueChange,
    onOpenChange,
    onClose,
    children,
    name,
    autoComplete,
    searchValue,
    searchDefaultValue,
    onSearchValueChange,
    disableFormAutoFill = false
  } = props;
  const [hasEmptyView, setHasEmptyView] = useState(false);
  const [inputFocused, setInputFocused] = useState(false);
  const [trigger, setTrigger] = useState(null);
  const isFormControl = trigger ? Boolean(trigger.closest("form")) : true;
  const [open = false, setOpen] = useControllableState({
    prop: openProp,
    defaultProp: defaultOpen,
    onChange: onOpenChange
  });
  const [value = "", setValue] = useControllableState({
    prop: valueProp,
    defaultProp: defaultValue,
    onChange: onValueChange
  });
  return /* @__PURE__ */ jsx(
    Legacy_Dropdown,
    {
      ...props,
      open,
      onOpenChange: setOpen,
      defaultOpen,
      children: /* @__PURE__ */ jsx(
        SelectSearchRoot,
        {
          value: searchValue,
          defaultValue: searchDefaultValue,
          onChangeValue: onSearchValueChange,
          children: /* @__PURE__ */ jsxs(
            SelectProvider,
            {
              value,
              onValueChange: setValue,
              open,
              onOpenChange: setOpen,
              trigger,
              onTriggerChange: setTrigger,
              inputFocused,
              onInputFocusChange: setInputFocused,
              hasEmptyView,
              onHasEmptyViewChange: setHasEmptyView,
              onClose,
              children: [
                children,
                !disableFormAutoFill && isFormControl ? /* @__PURE__ */ jsx(VisuallyHidden, { "data-role": "visually-hidden", children: /* @__PURE__ */ jsx(
                  BubbleSelect,
                  {
                    "aria-hidden": true,
                    tabIndex: -1,
                    name,
                    autoComplete,
                    value,
                    onChange: (event) => setValue(event.target.value)
                  }
                ) }) : null
              ]
            }
          )
        }
      )
    }
  );
};
const SelectSubMenu = (props) => {
  const { onValueChange, children, ...subMenuProps } = props;
  const context = useSelectContext();
  const searchContext = useSelectSearchContext();
  return /* @__PURE__ */ jsx(
    InternalSelect,
    {
      ...subMenuProps,
      value: context.value,
      onValueChange: composeEventHandlers(onValueChange, context.onValueChange),
      searchValue: searchContext.searchValue,
      onSearchValueChange: searchContext.onSearchValueChange,
      children: /* @__PURE__ */ jsx(Legacy_Dropdown.Sub, { children })
    }
  );
};
const StyledOption = styled(Option);
const SelectOption = forwardRef(
  ({ children, ...props }, ref) => {
    return /* @__PURE__ */ jsx(StyledOption, { ref, ...props, children: /* @__PURE__ */ jsx("div", { className: optionItemContentStyle, children }) });
  }
);
const SelectOption_legacy = forwardRef(({ children, selectIndicator, ...props }, ref) => {
  const indicator = useMemo(() => {
    return isValidElement(selectIndicator) ? selectIndicator : /* @__PURE__ */ jsx(SelectOptionIndicator, { style: { marginLeft: "auto" }, children: /* @__PURE__ */ jsx(CheckmarkIcon, { color: "green" }) });
  }, [selectIndicator]);
  return /* @__PURE__ */ jsx(Option, { ref, ...props, children: /* @__PURE__ */ jsxs("div", { className: optionLegacyItemContentStyle, children: [
    children,
    indicator
  ] }) });
});
const Legacy_Select = InternalSelect;
Legacy_Select.Trigger = Trigger;
Legacy_Select.Sub = SelectSubMenu;
Legacy_Select.SubTrigger = SelectSubMenuTrigger;
Legacy_Select.SubContent = SelectSubContent;
Legacy_Select.Value = SelectValue;
Legacy_Select.CapturedActiveValue = SelectCapturedActiveValue;
Legacy_Select.Content = SelectContent;
Legacy_Select.Option = SelectOption;
Legacy_Select.Group = SelectGroup;
Legacy_Select.Divider = SelectSeparator;
Legacy_Select.OptionTitle = OptionTitle;
Legacy_Select.OptionDescription = OptionDescription;
Legacy_Select.SearchInput = SearchInput;
Legacy_Select.StaticInput = StaticInput;
Legacy_Select.SearchEmpty = SelectSearchEmpty;
Legacy_Select.Aside = Legacy_Dropdown.Aside;
Legacy_Select.AffixRoot = AffixRoot;
Legacy_Select.OptionIndicator = SelectOptionIndicator;
Legacy_Select.Option_legacy = SelectOption_legacy;
Legacy_Select.OptionTitle_legacy = OptionTitle_legacy;

export { Legacy_Select };
//# sourceMappingURL=Select.mjs.map
